import { formatDistanceToNowStrict } from 'date-fns';
import { tv, type VariantProps } from 'tailwind-variants';
import { Image as CustomImage } from '@/app_components/common/Image';
import { Link } from '@/app_components/common/Link';
import { ButtonV2 } from '@/app_components/ui/ButtonV2';
import { DescriptionParser } from '@/app_lib/htmlParser/DescriptionParser';
import type { TrackEventParams } from '@/app_lib/services/Gtm/utils';
import type { ArticlePreview } from '@/app_lib/types/ArticlePreview';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn, formatDate } from '@/app_lib/utils/utils';

// biome-ignore lint/style/useComponentExportOnlyModules: <Using these styles for the loaders as well>
export const articleCard = tv(
  {
    slots: {
      articleDate: 'font-graphik typography-xs text-secondary w-fit',
      authorByline: 'typography-sm',
      authorLink: 'font-medium capitalize typography-sm',
      base: 'font-graphik group flex-1',
      excerpt: 'font-graphik-compact text-secondary line-clamp-2',
      eyebrow: 'typography-xs font-medium uppercase text-secondary hover:underline tracking-[2px] w-fit',
      image: 'aspect-[3/2] object-cover w-full h-full',
      imageContainer: 'aspect-[3/2] relative',
      timeAgo:
        'font-graphik-compact typography-sm font-medium bg-inverse py-xxxs px-xs rounded-sm text-white isolate z-body-popover',
      title: 'font-inria-serif font-bold w-fit',
    },
    variants: {
      variant: {
        featured: {
          base: 'flex flex-col gap-lg',
          excerpt: 'hidden md:block',
          title: 'typography-level-4',
        },
        grid: {
          base: 'flex flex-col gap-lg',
          title: 'typography-level-6',
        },
        hero: {
          // NOTE: Should this font size be set using a token?
          excerpt: 'text-lg',
          eyebrow: 'typography-sm',
          title: 'typography-level-2',
        },
        railFull: {
          base: 'flex flex-col gap-md',
          // NOTE: Is this the correct pixel value?
          image: 'w-[120px] h-fit',
          imageContainer: 'size-fit',
          title: 'flex-1 font-graphik-compact font-semibold line-clamp-5',
        },
        railSimple: {
          base: 'flex flex-col gap-2',
        },
        small: {
          base: 'flex flex-col md:flex-row gap-lg',
          // NOTE: Is this the correct pixel value?
          image: 'md:w-[160px] md:h-fit',
          imageContainer: 'size-fit',
          title: 'md:typography-level-6',
        },
        timed: {
          base: 'flex flex-col gap-lg relative',
          timeAgo: 'absolute -top-3 right-2',
          title: 'typography-level-6',
        },
        topic: {},
        trending: {
          base: 'flex lg:flex-col gap-md items-center lg:items-start',
        },
        vertical: {
          base: 'flex flex-col gap-lg',
          excerpt: 'hidden md:block',
          title: 'typography-level-5',
        },
      },
    },
  },
  {
    twMerge: true,
  },
);

export type ArticleCardVariants = VariantProps<typeof articleCard>;

export const ArticleCard = ({
  className,
  children,
  variant,
}: { className?: string; children?: React.ReactNode } & ArticleCardVariants) => (
  <div
    className={cn(articleCard().base({ variant }), className)}
    data-cy={ContentPlacement.ArticleWrapper}
  >
    {children}
  </div>
);

export const Image = ({
  className,
  image,
  imageAlt,
  priority,
  trackingData,
  url,
  variant,
}: { className?: string; priority?: boolean; trackingData?: TrackEventParams } & Pick<
  ArticlePreview,
  'image' | 'imageAlt' | 'url'
> &
  ArticleCardVariants) => (
  <Link
    className={cn(articleCard().imageContainer({ variant }), className)}
    href={url}
    trackingData={trackingData}
  >
    <CustomImage
      alt={imageAlt}
      className={articleCard().image({ variant })}
      data-cy={ContentPlacement.ArticleCardImage}
      priority={priority}
      src={image}
    />
    {/* TODO: Think about moving this into the Image component since it's reused often */}
    <div className='top-0 absolute bg-inverse opacity-0 hover:opacity-10 w-full h-full' />
  </Link>
);

export const Eyebrow = ({
  sectionName,
  sectionUrl,
  variant,
  trackingData,
}: { sectionName: string; sectionUrl: string; trackingData?: TrackEventParams } & ArticleCardVariants) =>
  sectionName &&
  sectionUrl && (
    <Link
      className={cn(articleCard().eyebrow({ variant }))}
      data-cy={ContentPlacement.ArticleEyebrow}
      href={sectionUrl}
      trackingData={trackingData}
    >
      {sectionName}
    </Link>
  );

export const Title = ({
  className,
  title,
  url,
  variant,
  trackingData,
}: { className?: string; trackingData?: TrackEventParams } & Pick<ArticlePreview, 'title' | 'url'> &
  ArticleCardVariants) => (
  <Link
    className={cn(articleCard().title({ variant }), className)}
    data-cy={ContentPlacement.ArticleTitle}
    href={url}
    recipe='nav'
    trackingData={trackingData}
    variant='primary'
  >
    {title}
  </Link>
);

export const Excerpt = ({ excerpt, variant }: Pick<ArticlePreview, 'excerpt'> & ArticleCardVariants) => (
  <div
    className={cn(articleCard().excerpt({ variant }))}
    data-cy={ContentPlacement.ArticleCardExcerpt}
  >
    <DescriptionParser content={excerpt} />
  </div>
);

export const AuthorByline = ({
  authors,
  variant,
  trackingData,
}: { trackingData?: TrackEventParams } & Pick<ArticlePreview, 'authors'> & ArticleCardVariants) => (
  <span
    className={cn(articleCard().authorByline({ variant }))}
    data-cy={ContentPlacement.ArticleCardByline}
  >
    <span data-cy={ContentPlacement.AuthorByText}>By </span>
    {authors
      .filter((author) => author.name && author.url)
      .map((author, index) => {
        let prependedContent = '';

        if (index > 0 && index === authors.length - 1) {
          prependedContent = ' and ';
        } else if (index > 0) {
          prependedContent = ', ';
        }

        return (
          <span key={author.url}>
            {prependedContent}
            <ButtonV2
              className={articleCard().authorLink({ variant })}
              data-cy={ContentPlacement.BylineAuthor}
              href={author.url}
              isTertiary
              trackingData={trackingData && { data: { ...trackingData, eventLabel: `${author.name} : ` } }}
            >
              {author.name}
            </ButtonV2>
          </span>
        );
      })}
  </span>
);

export const CardDate = ({ dateGmt, variant }: Pick<ArticlePreview, 'dateGmt'> & ArticleCardVariants) => (
  <span
    className={cn(articleCard().articleDate({ variant }))}
    data-cy={ContentPlacement.ArticleCardDate}
  >
    {formatDate(dateGmt)}
  </span>
);

export const CardTimeAgo = ({ dateGmt, variant }: Pick<ArticlePreview, 'dateGmt'> & ArticleCardVariants) => {
  return (
    <span
      className={articleCard().timeAgo({ variant })}
      data-cy={ContentPlacement.TimeAgo}
    >
      {formatDistanceToNowStrict(new Date(dateGmt), { addSuffix: true })}
    </span>
  );
};
